/**
 * usePermissions Hook
 *
 * React hook for checking user permissions in the frontend.
 *
 * Reads roles and permissions from the ID token's custom claims
 * (`custom:roles`, `custom:permissions`) injected by the Pre Token Generation
 * Lambda (see .kiro/specs/jwt-role-injection/design.md). Falls back to the
 * `getMyPermissions` GraphQL query when token claims are missing — preserving
 * backward compat for sessions minted before JWT role injection shipped.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { getSessionClaimsStatus, TokenClaims } from '../utils/tokenClaims';

// GraphQL query to get current user's permissions
const GET_MY_PERMISSIONS = gql`
  query GetMyPermissions {
    getMyPermissions {
      userId
      roles {
        roleId
        roleName
        description
        permissions
      }
      permissions
      effectivePermissions
    }
  }
`;

interface Role {
  roleId: string;
  roleName: string;
  description: string;
  permissions: string[];
}

interface UserPermissions {
  userId: string;
  roles: Role[];
  permissions: string[];
  effectivePermissions: string[];
}

interface UsePermissionsResult {
  permissions: string[];
  roles: Role[];
  loading: boolean;
  error: Error | undefined;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  hasRole: (roleName: string) => boolean;
  refetch: () => void;
}

/**
 * Match permission with wildcard support
 *
 * Examples:
 * - matchesPermission('campaigns:create', 'campaigns:create') => true
 * - matchesPermission('campaigns:*', 'campaigns:create') => true
 * - matchesPermission('*:*', 'campaigns:create') => true
 * - matchesPermission('*:read', 'campaigns:read') => true
 */
function matchesPermission(userPerm: string, requiredPerm: string): boolean {
  // Exact match
  if (userPerm === requiredPerm) {
    return true;
  }

  // Full wildcard
  if (userPerm === '*:*') {
    return true;
  }

  const [userResource, userAction] = userPerm.split(':');
  const [reqResource, reqAction] = requiredPerm.split(':');

  // Resource wildcard (e.g., campaigns:*)
  if (userResource === reqResource && userAction === '*') {
    return true;
  }

  // Action wildcard (e.g., *:read)
  if (userResource === '*' && userAction === reqAction) {
    return true;
  }

  return false;
}

/**
 * Build synthetic Role objects from role-name strings when only token claims
 * are available. Keeps the shape compatible with `hasRole(roleName)`, which
 * only reads `roleName`. Permission-level checks still use the flat
 * `permissions` array from the token.
 */
function rolesFromNames(names: string[]): Role[] {
  return names.map((name) => ({
    roleId: name,
    roleName: name,
    description: '',
    permissions: [],
  }));
}

/**
 * usePermissions Hook
 *
 * Resolves current user's permissions and roles — token first, GraphQL fallback.
 * Provides helper functions to check permissions.
 *
 * @example
 * ```tsx
 * function CampaignButton() {
 *   const { hasPermission, loading } = usePermissions();
 *
 *   if (loading) return <Spinner />;
 *
 *   if (!hasPermission('campaigns:create')) {
 *     return null; // Hide button if no permission
 *   }
 *
 *   return <button>Create Campaign</button>;
 * }
 * ```
 */
export function usePermissions(): UsePermissionsResult {
  const [tokenClaims, setTokenClaims] = useState<TokenClaims | null>(null);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  // Tracks whether there's a live Cognito session at all. Distinct from
  // "claims loaded" because an unauthenticated user has loaded claims
  // (empty arrays) but we must NOT fire the authenticated GraphQL fallback
  // for them — getMyPermissions requires auth and the query just fails.
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getSessionClaimsStatus()
      .then(({ claims, hasSession: sessionPresent }) => {
        if (cancelled) return;
        setTokenClaims(claims);
        setHasSession(sessionPresent);
        setClaimsLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        setTokenClaims({ roles: [], permissions: [] });
        setHasSession(false);
        setClaimsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const useTokenPath = !!(
    tokenClaims &&
    (tokenClaims.permissions.length > 0 || tokenClaims.roles.length > 0)
  );

  const { data, loading: gqlLoading, error, refetch } = useQuery<{ getMyPermissions: UserPermissions }>(
    GET_MY_PERMISSIONS,
    {
      fetchPolicy: 'cache-first',
      nextFetchPolicy: 'cache-first',
      // Skip the GraphQL call when:
      //   * claims haven't been resolved yet (avoid duplicate round-trip), OR
      //   * the token path is usable (JWT claims present), OR
      //   * the user has no Cognito session (pre-auth / logged-out) — the
      //     backend resolver requires authentication and the query would
      //     either 401 or leak noise into CloudWatch.
      skip: !claimsLoaded || useTokenPath || !hasSession,
    }
  );

  // Derive permissions — prefer token, fall back to GraphQL payload.
  const permissions = useMemo(() => {
    if (useTokenPath && tokenClaims) {
      return tokenClaims.permissions;
    }
    return data?.getMyPermissions?.effectivePermissions || [];
  }, [useTokenPath, tokenClaims, data]);

  const roles = useMemo<Role[]>(() => {
    if (useTokenPath && tokenClaims) {
      return rolesFromNames(tokenClaims.roles);
    }
    return data?.getMyPermissions?.roles || [];
  }, [useTokenPath, tokenClaims, data]);

  // Loading rule: we're done as soon as claims are loaded AND either:
  //   * we've decided on the token path, OR
  //   * there's no session to fall back on, OR
  //   * the GraphQL fallback has resolved.
  // Previously this treated empty claims + no session as "still loading",
  // which kept the query firing for unauthenticated users.
  const loading = !claimsLoaded || (hasSession && !useTokenPath && gqlLoading);

  useEffect(() => {
    console.log('[usePermissions] State:', {
      source: !hasSession ? 'unauthenticated' : useTokenPath ? 'token' : 'graphql',
      permissions,
      roles: roles.map((r) => r.roleName),
      loading,
      error: error?.message,
    });
  }, [useTokenPath, hasSession, permissions, roles, loading, error]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      if (!permission) return false;

      return permissions.some((userPerm) => matchesPermission(userPerm, permission));
    },
    [permissions]
  );

  /**
   * Check if user has any of the specified permissions (OR logic)
   */
  const hasAnyPermission = useCallback(
    (requiredPermissions: string[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return false;
      }

      return requiredPermissions.some((reqPerm) => hasPermission(reqPerm));
    },
    [hasPermission]
  );

  /**
   * Check if user has all of the specified permissions (AND logic)
   */
  const hasAllPermissions = useCallback(
    (requiredPermissions: string[]): boolean => {
      if (!requiredPermissions || requiredPermissions.length === 0) {
        return false;
      }

      return requiredPermissions.every((reqPerm) => hasPermission(reqPerm));
    },
    [hasPermission]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    (roleName: string): boolean => {
      if (!roleName) return false;

      return roles.some((role) => role.roleName.toLowerCase() === roleName.toLowerCase());
    },
    [roles]
  );

  return {
    permissions,
    roles,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    refetch: () => {
      // When on token path, refetch re-reads claims. Otherwise call GraphQL refetch.
      if (useTokenPath) {
        getSessionClaimsStatus().then(({ claims, hasSession: s }) => {
          setTokenClaims(claims);
          setHasSession(s);
        });
        return;
      }
      refetch();
    },
  };
}

/**
 * usePermissionCheck Hook
 *
 * Check a specific permission with server-side validation.
 * Use this when you need real-time permission checking.
 *
 * @example
 * ```tsx
 * function DeleteButton({ campaignId }) {
 *   const { hasPermission, loading } = usePermissionCheck('campaigns:delete');
 *
 *   if (loading || !hasPermission) return null;
 *
 *   return <button onClick={() => deleteCampaign(campaignId)}>Delete</button>;
 * }
 * ```
 */
export function usePermissionCheck(permission: string) {
  // Reuse usePermissions so we inherit the same short-circuits:
  //   * unauthenticated users never hit the network
  //   * users with JWT claims resolve locally
  //   * only users without claims (legacy sessions) go to GraphQL
  const { hasPermission, loading, error } = usePermissions();

  return {
    hasPermission: permission ? hasPermission(permission) : false,
    loading,
    error,
    deniedReason: undefined as string | undefined,
  };
}

/**
 * PermissionGate Component
 *
 * Conditionally render children based on permission check.
 *
 * @example
 * ```tsx
 * <PermissionGate permission="campaigns:create">
 *   <CreateCampaignButton />
 * </PermissionGate>
 *
 * <PermissionGate
 *   anyPermission={['campaigns:update', 'campaigns:delete']}
 *   fallback={<div>No access</div>}
 * >
 *   <CampaignActions />
 * </PermissionGate>
 * ```
 */
interface PermissionGateProps {
  children: React.ReactNode;
  permission?: string;
  anyPermission?: string[];
  allPermissions?: string[];
  role?: string;
  fallback?: React.ReactNode;
  loading?: React.ReactNode;
}

export function PermissionGate({
  children,
  permission,
  anyPermission,
  allPermissions,
  role,
  fallback = null,
  loading: loadingComponent = null,
}: PermissionGateProps) {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    loading,
  } = usePermissions();

  if (loading) {
    return loadingComponent as React.ReactElement;
  }

  let hasAccess = false;

  if (permission) {
    hasAccess = hasPermission(permission);
  } else if (anyPermission) {
    hasAccess = hasAnyPermission(anyPermission);
  } else if (allPermissions) {
    hasAccess = hasAllPermissions(allPermissions);
  } else if (role) {
    hasAccess = hasRole(role);
  }

  return hasAccess ? (children as React.ReactElement) : (fallback as React.ReactElement);
}

export default usePermissions;
